import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { redirectByRole } from '../../utils/roleRedirect';
import { getStoredAuthUser, hasStoredAuthSession } from '../../utils/authStorage';
import mpsLogo from '../../assets/images/mps_logo.png';
import ScrollReveal from '../Landing/ScrollReveal';
import '../../styles/form.css';
import '../Landing/landing-animations.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login, loading, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated (context or persisted session)
  useEffect(() => {
    if (loading) {
      return;
    }

    const sessionUser = user || getStoredAuthUser();
    if ((isAuthenticated || hasStoredAuthSession()) && sessionUser) {
      redirectByRole(sessionUser.role, navigate);
    }
  }, [isAuthenticated, loading, user, navigate]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success && result.data?.next_step === 'SETUP_2FA') {
        navigate('/2fa/setup', { replace: true });
        return;
      }

      if (result.success && result.data?.next_step === 'VERIFY_OTP') {
        navigate('/2fa/verify', { replace: true });
        return;
      }

      if (result.success && result.data?.user) {
        // Immediately redirect based on role after successful login
        redirectByRole(result.data.user.role, navigate);
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render login form if user is already authenticated
  if (isAuthenticated && !loading) {
    return null;
  }

  return (
    <div 
      className="min-vh-100 d-flex flex-column brand-app-login-page" 
      style={{ 
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    >
      {/* Add ScrollReveal component */}
      <ScrollReveal />
      
      {/* Main Content */}
      <div className="flex-grow-1 d-flex align-items-center justify-content-center py-5 brand-app-login-content" 
           style={{ paddingTop: '100px' }}
      >
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} sm={8} md={6} lg={4} xl={4}>
            <Card className="shadow-lg border-0" style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}>
              {/* Logo Header Section */}
              <div 
                className="text-center py-3 px-4" 
                style={{ 
                  backgroundColor: '#ffffff',
                  borderBottom: '1px solid #e0e0e0',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px'
                }}
              >
                <img 
                  src={mpsLogo}
                  alt="Enable LXP Logo" 
                  style={{ 
                    width: '110px', 
                    height: '52px', 
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto 0.5rem'
                  }}
                />
                <h3 className="fw-bold text-dark mb-1">Welcome Back</h3>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>Sign in to your account to continue</p>
              </div>
              
              <Card.Body className="p-4">

                {/* Login Form */}
                <Form onSubmit={handleSubmit}>
                  {/* Email Field */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold mb-2" style={{ color: '#2c2c2e', fontSize: '14px' }}>
                      Email Address
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      isInvalid={!!errors.email}
                      style={{ 
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        fontSize: '15px',
                        padding: '10px 14px',
                        transition: 'all 0.3s ease',
                        boxShadow: 'none'
                      }}
                      className="brand-focus-ring"
                      disabled={isSubmitting || loading}
                    />
                    <Form.Control.Feedback type="invalid" style={{ fontSize: '13px' }}>
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Password Field */}
                  <Form.Group className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="fw-semibold mb-0" style={{ color: '#2c2c2e', fontSize: '14px' }}>
                        Password
                      </Form.Label>
                      <a 
                        href="/forgot-password" 
                        className="text-decoration-none brand-link-accent"
                        style={{ fontSize: '13px', fontWeight: '500' }}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/forgot-password');
                        }}
                      >
                        Forgot Password?
                      </a>
                    </div>
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                        isInvalid={!!errors.password}
                        style={{ 
                          borderRadius: '6px',
                          border: '1px solid #e0e0e0',
                          fontSize: '15px',
                          padding: '10px 45px 10px 14px',
                          transition: 'all 0.3s ease',
                          boxShadow: 'none'
                        }}
                        className="brand-focus-ring"
                        disabled={isSubmitting || loading}
                      />
                      <button
                        type="button"
                        className="position-absolute border-0 bg-transparent"
                        style={{ 
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#6c757d',
                          fontSize: '16px',
                          cursor: 'pointer',
                          padding: '4px',
                          outline: 'none'
                        }}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isSubmitting || loading}
                        onMouseEnter={(e) => {
                          e.target.style.color = 'var(--brand-red)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#6c757d';
                        }}
                      >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                    <Form.Control.Feedback type="invalid" style={{ fontSize: '13px' }}>
                      {errors.password}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-100 fw-semibold mt-3 brand-app-login-btn"
                    disabled={isSubmitting || loading}
                    style={{
                      borderRadius: '6px',
                      padding: '11px 16px',
                      fontSize: '15px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting && !loading) {
                        e.target.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting && !loading) {
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {isSubmitting || loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Signing In...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </Form>

                {/* Additional Info */}
                <div className="text-center mt-3">
                  <small className="text-muted" style={{ fontSize: '12px' }}>
                    By signing in, you agree to our Terms of Service and Privacy Policy
                  </small>
                  
                  {/* Back to Home link */}
                  <div className="mt-3">
                    <Link 
                      to="/" 
                      className="d-inline-block text-decoration-none brand-link-accent" 
                      style={{ fontWeight: '500', fontSize: '14px' }}
                    >
                      <i className="fas fa-arrow-left me-1"></i> Back to Home
                    </Link>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      </div>
    </div>
  );
};

export default Login;